// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/lp/:slug\",\"regex\":\"^/lp/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/:entity*\",\"regex\":\"^/(.+)$\",\"paramNames\":[\"entity\"],\"hasOptionalPrefix\":false},{\"path\":\"/podsumowanie/:id\",\"regex\":\"^/podsumowanie/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/wytworcy/:entity*\",\"regex\":\"^/wytworcy/(.+)$\",\"paramNames\":[\"entity\"],\"hasOptionalPrefix\":false},{\"path\":\"/cms/:index*?\",\"regex\":\"^/cms(?:/(.*))?$\",\"paramNames\":[\"index\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/login/apple/callback\"},{\"path\":\"/login/google/callback\"},{\"path\":\"/logowanie\"},{\"path\":\"/rejestracja\"},{\"path\":\"/reset-hasla\"},{\"path\":\"/weryfikacja\"},{\"path\":\"/zmiana-adresu-email\"},{\"path\":\"/zmiana-hasla\"},{\"path\":\"/faq\"},{\"path\":\"/gwarancja-jakosci\"},{\"path\":\"/jak-zalozyc-konto\"},{\"path\":\"/jak-zlozyc-zamowienie\"},{\"path\":\"/o-dostawie\"},{\"path\":\"/o-dostawie/koszty-dostawy\"},{\"path\":\"/o-dostawie/metody-platnosci\"},{\"path\":\"/o-dostawie/obszary-dostawy\"},{\"path\":\"/o-dostawie/terminy-i-metody-dostawy\"},{\"path\":\"/o-nas\"},{\"path\":\"/odstapienie-od-umowy\"},{\"path\":\"/program-nagrod\"},{\"path\":\"/wspolpraca\"},{\"path\":\"/\"},{\"path\":\"/_promocje-legacy\"},{\"path\":\"/dostawa\"},{\"path\":\"/kategorie\"},{\"path\":\"/koszyk\"},{\"path\":\"/najczesciej-kupowane\"},{\"path\":\"/nowosci\"},{\"path\":\"/platnosc\"},{\"path\":\"/polityka-prywatnosci\"},{\"path\":\"/produkty\"},{\"path\":\"/profil\"},{\"path\":\"/profil/adresy-dostaw\"},{\"path\":\"/profil/dane\"},{\"path\":\"/profil/dane-faktury\"},{\"path\":\"/profil/kody-rabatowe\"},{\"path\":\"/profil/program-nagrod\"},{\"path\":\"/profil/ulubione\"},{\"path\":\"/profil/zamowienia\"},{\"path\":\"/regulamin\"},{\"path\":\"/sitemap\"},{\"path\":\"/specjaly\"},{\"path\":\"/szukaj\"},{\"path\":\"/wypisanie-z-newslettera\"},{\"path\":\"/wytworcy\"},{\"path\":\"/zapis-do-newslettera\"},{\"path\":\"/dozobaczenia\"},{\"path\":\"/rezygnacja\"},{\"path\":\"/wsparcie\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.4.2";globalThis["_sentryRewritesTunnelPath"] = "/monitoring";import type { Event } from '@sentry/nextjs'
import * as Sentry from '@sentry/nextjs'
import axios, { AxiosError } from 'axios'

import { getEnv, isBeta, isDev, isProd } from 'shared/utils/env'
import { getErrorMessage } from 'shared/utils/errors'

function addAxiosContext(event: Event, error: AxiosError) {
  if (error?.response) {
    // Safely handle null/undefined contexts
    const contexts = event.contexts ? { ...event.contexts } : {}

    contexts.Axios = { Response: error.response }
    event.contexts = contexts
  }
}

function isMinifedHydrationError(originalException: unknown): boolean {
  if (originalException instanceof Error && originalException.message) {
    const message = originalException.message
    const reactErrorMinifedMatch = message.match(/Minified React error #(\d+)/)

    if (reactErrorMinifedMatch) {
      const errorCode = reactErrorMinifedMatch[1]
      const hydrationErrorCodes = [
        '418', // Hydration failed on the client.
        '422', // Mismatch between server and client HTML.
        '423', // Mismatch in a text node.
        '425' // Text content does not match server-rendered HTML.
      ]

      if (hydrationErrorCodes.includes(errorCode)) {
        return true
      }
    }
  }

  return false
}

/**
 * Shipped chunks are syntactically valid by construction (webpack would fail
 * the build otherwise) — a script-parse SyntaxError pointing at /_next/static/
 * means the server/CDN returned something else (error page HTML) for a stale
 * chunk URL after a deploy. Auto-reload recovery below handles the UX; the
 * event itself is unactionable noise.
 *
 * Runtime SyntaxErrors thrown BY app code must keep flowing to Sentry, so two
 * discriminators are required: JSON.parse failures always mention "JSON" in
 * the message (V8 and WebKit alike), and runtime throws keep their caller
 * stack, while a script-parse error surfaces as a lone frame — the failing
 * script itself.
 */
function isStaleChunkParseError(event: Event): boolean {
  const values = event.exception?.values

  if (!values || values.length === 0) return false

  return values.every(exceptionValue => {
    if (exceptionValue.type !== 'SyntaxError') return false
    if (/json/i.test(String(exceptionValue.value || ''))) return false

    const frames = exceptionValue.stacktrace?.frames || []

    return (
      frames.length > 0 &&
      frames.length <= 2 &&
      frames.every(frame =>
        /\/_next\/static\//i.test(String(frame.filename || ''))
      )
    )
  })
}

const externalsErrorMessages = [
  /disabled chat/i,
  /ResizeObserver loop limit exceeded/i,
  /Using default logger/i,
  /Install new widget chat/i,
  /Amplitude Engagement Logger/i,
  /Element with name "gmp-place-autocomplete" already defined./i,
  /Amplitude Logger/i,
  /Synerise/i,
  /SyneriseTC/i,
  /SR.init/i,
  /^clarity/i,
  /measurement-api.criteo.com/i,
  /Java object is gone/i,
  /Cannot read properties of null \(reading 'tagName'\)/i,
  /Prohibited read from data layer variable/i
]
// Common hydration error messages to group together
const ignoreErrorMessages = [
  /This Suspense boundary received an update before it finished hydrating/i,
  /React was able to recover by instead client rendering/i,
  /Hydration failed/i,
  /Text content does not match server-rendered HTML/i,
  /didn['’]t match the client/i,
  /A tree hydrated but some attributes of the server rendered HTML didn't match the client/i,
  /There was an error while hydrating/i,
  /react hydration/i,
  /hydration/i,
  /React does not recognize the/i,
  /Received `true` for a non-boolean attribute/i,
  /ChunkLoadError/i,
  /Loading chunk/i,
  /Failed to read the 'cookie' property from 'Document'/i,
  /document is sandboxed/i,
  /allow-same-origin/i
]

// Dropped only when no frame comes from our bundle (e.g. Chrome iOS injected scripts)
const unknownSourceNoiseMessages = [/Maximum call stack size exceeded/i]

type ErrorSource = 'app' | 'external' | 'mixed' | 'unknown'

const thirdPartyScriptMarkers = [
  /googletagmanager/i,
  /google-analytics/i,
  /clarity/i,
  /synerise/i,
  /criteo/i,
  /facebook|fbevents/i,
  /hotjar/i
]

const isAppFrame = (filename: string, appOrigin: string): boolean => {
  const normalized = filename.toLowerCase()

  return (
    normalized.startsWith('app:///_next') ||
    normalized.includes('/_next/static/') ||
    normalized.startsWith('webpack-internal://') ||
    normalized.startsWith('webpack://') ||
    (!!appOrigin && normalized.startsWith(appOrigin))
  )
}

const isExternalFrame = (filename: string, appOrigin: string): boolean => {
  const normalized = filename.toLowerCase()

  if (
    normalized.includes('externals/') ||
    normalized.includes('app:///externals/') ||
    thirdPartyScriptMarkers.some(marker => marker.test(normalized))
  ) {
    return true
  }

  if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
    try {
      return new URL(normalized).origin !== appOrigin
    } catch {
      return false
    }
  }

  return false
}

function classifyErrorSource(event: Event): ErrorSource {
  if (typeof window === 'undefined') return 'unknown'

  const appOrigin = window.location.origin.toLowerCase()
  const values = event.exception?.values

  if (!Array.isArray(values)) return 'unknown'

  let hasAppFrame = false
  let hasExternalFrame = false

  for (const exceptionValue of values) {
    for (const frame of exceptionValue.stacktrace?.frames || []) {
      const filename = String(frame.filename || '')

      if (!filename) continue

      if (isAppFrame(filename, appOrigin)) hasAppFrame = true
      if (isExternalFrame(filename, appOrigin)) hasExternalFrame = true
    }
  }

  if (hasAppFrame && hasExternalFrame) return 'mixed'
  if (hasAppFrame) return 'app'
  if (hasExternalFrame) return 'external'

  return 'unknown'
}

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  sendDefaultPii: true,
  environment: getEnv(),
  // Adjust this value in production, or use tracesSampler for greater control
  tracesSampleRate: isProd ? 0.1 : 1.0,
  sampleRate: isProd ? 0.1 : 1.0,
  enabled: isProd || isBeta || isDev,
  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: process.env.NEXT_PUBLIC_SENTRY_DEBUG === 'true',
  replaysOnErrorSampleRate: isProd ? 0.1 : 1.0,
  // Enable source maps for better error tracking
  attachStacktrace: true,
  // Add more context to errors
  maxBreadcrumbs: 50,
  // Add more context to errors
  normalizeDepth: 5,
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: isProd ? 0.1 : 1.0,

  ignoreErrors: [...externalsErrorMessages, ...ignoreErrorMessages],
  denyUrls: [
    /chrome-extension:\/\//i,
    /moz-extension:\/\//i,
    /extensions\//i,
    /googletagmanager\.com/i,
    /google-analytics\.com/i,
    /clarity\.ms/i,
    /criteo\.com/i,
    /hotjar\.com/i,
    /facebook\.net/i
  ],

  beforeSend(event, hint) {
    const originalException = hint.originalException

    if (isMinifedHydrationError(originalException)) {
      return null
    }

    if (isStaleChunkParseError(event)) {
      return null
    }

    const collectEventText = (): string => {
      const chunks: string[] = []

      if (event.message) chunks.push(String(event.message))

      if (originalException instanceof Error && originalException.message) {
        chunks.push(String(originalException.message))
      } else if (typeof originalException === 'string') {
        chunks.push(originalException)
      }

      const values = event.exception?.values

      if (Array.isArray(values)) {
        for (const v of values) {
          if (v.value) chunks.push(String(v.value))
          if (v.type) chunks.push(String(v.type))
        }
      }

      return chunks.join(' | ')
    }

    const text = collectEventText()

    if (text) {
      const isIgnoringErrors = [
        ...externalsErrorMessages,
        ...ignoreErrorMessages
      ].some(regex => regex.test(text))

      if (isIgnoringErrors) return null
    }

    const errorSource = classifyErrorSource(event)

    // Drop only confidently external errors. Keep mixed/unknown to avoid
    // losing real app issues when browser stack traces are incomplete.
    if (errorSource === 'external') return null

    // Unknown-source events are dropped only for known injected-script noise
    if (
      errorSource === 'unknown' &&
      unknownSourceNoiseMessages.some(regex => regex.test(text))
    ) {
      return null
    }

    event.tags = {
      ...event.tags,
      error_source: errorSource
    }

    // Add Axios errors context
    if (axios.isAxiosError(hint.originalException)) {
      addAxiosContext(event, hint.originalException)
    }

    return event
  },

  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: true,
      maskAllInputs: true
    })
  ]
})

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart

/** After a new deploy, old tabs may request removed chunk URLs; reload once to fetch fresh HTML. */
if (typeof window !== 'undefined') {
  const CHUNK_RELOAD_SESSION_KEY = '__chunk_load_reload_attempted'
  // Loop guard: at most one recovery reload per interval. A time window (not
  // a clear-on-load flag) keeps repeated failures bounded even when each
  // reload itself succeeds and the stale chunk fails again on interaction.
  const CHUNK_RELOAD_MIN_INTERVAL_MS = 60_000

  const isChunkLoadFailure = (reason: unknown): boolean => {
    if (reason instanceof Error && reason.name === 'ChunkLoadError') return true

    const message = getErrorMessage(reason).toLowerCase()

    return (
      message.includes('loading chunk') ||
      message.includes('chunkloaderror') ||
      message.includes('failed to fetch dynamically imported module')
    )
  }

  const scheduleChunkRecoveryReload = (): void => {
    try {
      const now = Date.now()
      const rawLastAttempt = Number(
        sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) || 0
      )
      // Garbage or future values (legacy flag, clock rollback) must never
      // suppress recovery — treat them as "no previous attempt".
      const lastAttempt =
        Number.isFinite(rawLastAttempt) && rawLastAttempt <= now
          ? rawLastAttempt
          : 0

      if (now - lastAttempt < CHUNK_RELOAD_MIN_INTERVAL_MS) return

      sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, String(now))
    } catch {
      return
    }

    window.location.reload()
  }

  // Positive evidence before reloading: re-fetch the failing script and treat
  // it as stale only when the server does not answer with valid JavaScript
  // (deploy gap → error page/HTML). A runtime SyntaxError thrown by code
  // inside a healthy chunk re-fetches as proper JS and never reloads.
  const verifyChunkIsStale = async (chunkUrl: string): Promise<boolean> => {
    try {
      const response = await fetch(chunkUrl, { cache: 'no-store' })
      const contentType = response.headers.get('content-type') || ''

      return !response.ok || !/javascript|ecmascript/i.test(contentType)
    } catch {
      // Network failure on a chunk URL — unrecoverable without a reload too.
      return true
    }
  }

  // Script parse errors from stale chunks surface via window.onerror as
  // SyntaxError with the chunk file as `filename` (the server returned an
  // error page instead of JS) — they never match ChunkLoadError patterns.
  const isStaleChunkSyntaxError = (event: ErrorEvent): boolean =>
    /\/_next\/static\//i.test(event.filename || '') &&
    // JSON.parse SyntaxErrors always mention "JSON" — those are app errors,
    // not stale-chunk parse failures; never reload on them.
    !/json/i.test(String(event.message || '')) &&
    (event.error instanceof SyntaxError ||
      /syntaxerror|unexpected token/i.test(String(event.message || '')))

  window.addEventListener('error', event => {
    if (isChunkLoadFailure(event.error ?? event.message)) {
      scheduleChunkRecoveryReload()

      return
    }

    if (isStaleChunkSyntaxError(event)) {
      void verifyChunkIsStale(event.filename).then(isStale => {
        if (isStale) scheduleChunkRecoveryReload()
      })
    }
  })

  window.addEventListener('unhandledrejection', event => {
    if (isChunkLoadFailure(event.reason)) {
      scheduleChunkRecoveryReload()
    }
  })
}
